import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import {
  FiRefreshCw,
  FiCheckCircle,
  FiAlertCircle,
  FiSearch,
  FiTruck,
  FiMail,
  FiExternalLink,
  FiXCircle,
  FiEdit3,
} from 'react-icons/fi';
import RejectMoverVerificationModal from './RejectMoverVerificationModal';

const isDemenageurRole = (role) => role === 'demenageur' || role === 'mover';

const getVerificationStatus = (u) => {
  if (u.is_verified) return 'verified';
  if (u.verification_status === 'rejected') return 'rejected';
  if (u.verification_status === 'resubmitted') return 'resubmitted';
  return 'pending';
};

const isAwaitingReview = (u) => {
  const status = getVerificationStatus(u);
  return status === 'pending' || status === 'resubmitted';
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('fr-FR');
};

function VerificationStatusBadge({ user }) {
  const status = getVerificationStatus(user);
  if (status === 'verified') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-green-900/40 text-green-400 text-xs font-medium border border-green-700/50">
        <FiCheckCircle className="w-3.5 h-3.5" />
        Vérifié
      </span>
    );
  }
  if (status === 'rejected') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-900/40 text-red-400 text-xs font-medium border border-red-700/50">
        <FiXCircle className="w-3.5 h-3.5" />
        Refusé
      </span>
    );
  }
  if (status === 'resubmitted') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-900/40 text-blue-300 text-xs font-medium border border-blue-700/50">
        <FiEdit3 className="w-3.5 h-3.5" />
        Dossier corrigé
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-900/40 text-amber-400 text-xs font-medium border border-amber-700/50">
      <FiAlertCircle className="w-3.5 h-3.5" />
      En attente
    </span>
  );
}

function AccountVerification() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('pending'); // pending | rejected | all
  const [verifyingId, setVerifyingId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getAllUsers();
      if (res?.success) {
        setUsers(Array.isArray(res.data) ? res.data : []);
      } else {
        throw new Error(res?.message || 'Impossible de charger les utilisateurs');
      }
    } catch (e) {
      console.error(e);
      setError(e.message || 'Erreur de chargement');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const movers = useMemo(
    () => users.filter((u) => isDemenageurRole(u.role)),
    [users]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = movers;
    if (tab === 'pending') {
      list = movers.filter(isAwaitingReview);
    } else if (tab === 'rejected') {
      list = movers.filter((u) => getVerificationStatus(u) === 'rejected');
    }
    if (!q) return list;
    return list.filter(
      (u) =>
        `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.phone || '').toLowerCase().includes(q)
    );
  }, [movers, tab, search]);

  const pendingCount = useMemo(() => movers.filter(isAwaitingReview).length, [movers]);
  const rejectedCount = useMemo(
    () => movers.filter((u) => getVerificationStatus(u) === 'rejected').length,
    [movers]
  );

  const handleRejected = async () => {
    setRejectTarget(null);
    await load();
  };

  const handleVerify = async (userId) => {
    if (!window.confirm('Valider ce compte déménageur et le passer en disponible ?')) return;
    try {
      setVerifyingId(userId);
      const res = await api.verifyDemenageurAccount(userId);
      if (res?.success) {
        await load();
      } else {
        throw new Error(res?.message || 'Échec de la vérification');
      }
    } catch (e) {
      alert(e.message || 'Erreur lors de la vérification');
    } finally {
      setVerifyingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white p-6 lg:p-10">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-white flex items-center gap-3">
              <FiCheckCircle className="text-yellow-500 w-8 h-8" />
              Vérification des comptes
            </h1>
            <p className="text-gray-400 mt-2">
              Validez les comptes déménageurs en attente (documents / inscription).
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gray-800 border border-gray-600 text-gray-200 hover:bg-gray-700 hover:border-yellow-600/50 transition-colors disabled:opacity-50"
          >
            <FiRefreshCw className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
        </div>

        <div className="flex flex-wrap gap-3 mb-6">
          <button
            type="button"
            onClick={() => setTab('pending')}
            className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${
              tab === 'pending'
                ? 'bg-yellow-600/20 border-yellow-500 text-yellow-400'
                : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-600'
            }`}
          >
            En attente
            {pendingCount > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-yellow-600/30 text-yellow-300 text-xs">
                {pendingCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setTab('rejected')}
            className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${
              tab === 'rejected'
                ? 'bg-red-600/20 border-red-500 text-red-400'
                : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-600'
            }`}
          >
            Refusés
            {rejectedCount > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-red-600/30 text-red-300 text-xs">
                {rejectedCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setTab('all')}
            className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${
              tab === 'all'
                ? 'bg-yellow-600/20 border-yellow-500 text-yellow-400'
                : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-600'
            }`}
          >
            Tous les déménageurs
          </button>
        </div>

        <div className="relative mb-6">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
          <input
            type="search"
            placeholder="Rechercher par nom, email ou téléphone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-11 pr-4 py-3 rounded-xl bg-gray-900/80 border border-gray-700 text-white placeholder-gray-500 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
          />
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-900/30 border border-red-700/50 text-red-300 flex items-start gap-3">
            <FiAlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-4 border-t-yellow-500 border-yellow-500/30 rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 rounded-2xl border border-gray-700 bg-gray-900/40">
            <FiTruck className="w-14 h-14 mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400">
              {tab === 'pending'
                ? 'Aucun déménageur en attente de vérification.'
                : tab === 'rejected'
                  ? 'Aucun compte refusé.'
                  : 'Aucun déménageur ne correspond à la recherche.'}
            </p>
          </div>
        ) : (
          <div className="rounded-2xl border border-gray-700/80 overflow-hidden bg-gray-900/50 shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-700 bg-gray-900/90">
                    <th className="px-4 py-3 font-semibold text-gray-300">Déménageur</th>
                    <th className="px-4 py-3 font-semibold text-gray-300 hidden md:table-cell">Email</th>
                    <th className="px-4 py-3 font-semibold text-gray-300 hidden lg:table-cell">Statut</th>
                    <th className="px-4 py-3 font-semibold text-gray-300 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => (
                    <tr
                      key={u.id}
                      className="border-b border-gray-800/80 hover:bg-gray-800/40 transition-colors"
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-green-600/20 border border-green-500/40 flex items-center justify-center flex-shrink-0">
                            <FiTruck className="text-green-400 w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-medium text-white">
                              {[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}
                            </p>
                            <p className="text-xs text-gray-500 md:hidden flex items-center gap-1 mt-0.5">
                              <FiMail className="w-3 h-3" />
                              {u.email || '—'}
                            </p>
                            <div className="lg:hidden mt-1">
                              <VerificationStatusBadge user={u} />
                            </div>
                            {!u.is_verified && u.verification_rejection_reason && (
                              <p className="mt-1 max-w-md text-xs text-red-300/90 whitespace-normal">
                                {getVerificationStatus(u) === 'resubmitted' ? 'Ancienne raison du refus : ' : 'Raison du refus : '}
                                {u.verification_rejection_reason}
                              </p>
                            )}
                            {getVerificationStatus(u) === 'resubmitted' && u.verification_resubmitted_at && (
                              <p className="mt-0.5 text-xs text-blue-300/80">
                                Corrigé le {formatDateTime(u.verification_resubmitted_at)}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-gray-300 hidden md:table-cell">{u.email || '—'}</td>
                      <td className="px-4 py-4 hidden lg:table-cell">
                        <span className="text-gray-400 text-xs mr-2">{u.status || '—'}</span>
                        <VerificationStatusBadge user={u} />
                      </td>
                      <td className="px-4 py-4 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                          <Link
                            to={`/admin/mover-registration/${u.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-800 border border-gray-600 text-gray-300 hover:border-yellow-600/50 hover:text-yellow-400 text-xs font-medium"
                          >
                            Fiche
                            <FiExternalLink className="w-3 h-3" />
                          </Link>
                          {!u.is_verified && (
                            <button
                              type="button"
                              disabled={verifyingId === u.id}
                              onClick={() => handleVerify(u.id)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gradient-to-r from-yellow-600 to-yellow-500 text-gray-900 font-semibold text-xs hover:from-yellow-500 hover:to-yellow-400 disabled:opacity-60"
                            >
                              {verifyingId === u.id ? (
                                <>
                                  <FiRefreshCw className="w-3.5 h-3.5 animate-spin" />
                                  Validation…
                                </>
                              ) : (
                                <>
                                  <FiCheckCircle className="w-3.5 h-3.5" />
                                  Valider
                                </>
                              )}
                            </button>
                          )}
                          {isAwaitingReview(u) && (
                            <button
                              type="button"
                              disabled={verifyingId === u.id}
                              onClick={() => setRejectTarget(u)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-600/90 text-white font-semibold text-xs hover:bg-red-500 disabled:opacity-60"
                            >
                              <FiXCircle className="w-3.5 h-3.5" />
                              Refuser
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <RejectMoverVerificationModal
        mover={rejectTarget}
        onClose={() => setRejectTarget(null)}
        onRejected={handleRejected}
      />
    </div>
  );
}

export default AccountVerification;
